//#region Imports and Global Definitions
import React, { useState, useEffect, } from 'react';
import { StyleSheet, SafeAreaView, Platform, useWindowDimensions, ScrollView, Image, View, FlatList, Pressable } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../../components/CustomInput';
import CustomButton from '../../../../components/CustomButton';
import { useCompany } from '../../../../hooks/useCompany';
import { showMessage } from "react-native-flash-message";
var _ = require('lodash');
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../../../hooks/useSettings';
import CustomControlledCheckbox from '../../../../components/ControlledComponents/CustomControlledCheckbox';
import CustomControlledInput from '../../../../components/ControlledComponents/CustomControlledInput';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useHRAdminEmployees } from '../../../../hooks/useHRAdminEmployees';
import { ActivityIndicator } from 'react-native-paper';
//#endregion

const HRAdminUserRoles = (props) => {

    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const { getUserRoles } = useCompany();
    const { updateUserRole, issuePassword, sendUsername } = useHRAdminEmployees();
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [userRolesOriginal, setUserRolesOriginal] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [selectedUserRole, setSelectedUserRole] = useState('');
    const { t } = useTranslation();
    const { control, handleSubmit, formState: { errors }, setValue } = useForm({
        defaultValues: {
            name: props.employee?.name,
            username: props.employee?.username,
            userroleid: null,
            password: null,
            resetPass: false,
            lockUser: false,
            employeeId: props.employee.employeeID
        },
    });
    //#endregion

    //#region useEffect
    useEffect(() => {
        fetchUserRoles();
    }, [])

    useEffect(() => {
        if (searchQuery !== undefined) {
            findUserRoles(searchQuery);
        }
    }, [searchQuery]);
    //#endregion

    //#region Helpers
    const findUserRoles = (searchQuery = '') => {
        let userroles = userRolesOriginal.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()));
        setUserRoles(userroles);
    }

    const fetchUserRoles = () => {
        setIsLoading(true);
        getUserRoles().then((res) => {
            let userroles = res?.data;
            let userroleTemp = prepareDropdown(userroles, 'userrole', 'userroleid');
            setUserRoles(userroleTemp);
            setUserRolesOriginal(userroleTemp);
            setIsLoading(false);
        }).catch(() => {
            console.error('User Roles were not fetched!')
            setUserRoles([]);
            setUserRolesOriginal([]);
            setIsLoading(false);
        })
    }

    const prepareDropdown = (arr, label, value) => {
        let tempArr = [];
        arr.forEach((obj) => {
            tempArr.push({ label: obj[label], value: obj[value] });
        });
        return tempArr;
    }
    //#endregion

    //#region Listeners
    const onSubmit = data => {
        updateUserRole(data).then((res) => {
            if (res.status === 200) {
                showMessage({
                    message: t('common:emplUserRoleSavedSuccMess'),
                    type: "success",
                    duration: 3000,
                })
                props.changeStep(props.employee);
            }
            else {
                showMessage({
                    message: t('common:timeschemesErr'),
                    type: "success",
                    duration: 3000,
                })
            }
        })
    };

    const [showErrors, setShowErrors] = useState({});
    // if required fields are not filled the below will be called.
    const onErrorsFound = data => {
        setShowErrors(data);
        return (
            showMessage({
                message: t('common:errReqFieldsNotFilled'),
                type: "danger",
                duration: 3000,
            })
        )
    }

    const onIssuePassword = () => {
        issuePassword(props.employee.name, props.employee.employeeID, props.employee.username).then((res) => {
            const data = res?.data?.[0];
            if (!data) {
                showMessage({
                    message: t(`common:errorUpdating`),
                    type: "danger",
                    duration: 3000,
                })
                return;
            }
            const pass = data?.password ?? '';
            const encryptedPassword = data?.encryptedPassword ?? '';
            const isError = data?.isError ?? false;
            const status = res?.status;
            const resetPass = data?.resetPass ?? false;
            if (status === 200) {
                showMessage({
                    message: t(`common:issuedPassSucc`),
                    type: isError ? "danger" : "success",
                    duration: 3000,
                })
            }
            else {
                showMessage({
                    message: t(`common:errorUpdating`),
                    type: isError ? "danger" : "success",
                    duration: 3000,
                })
            }
            setValue('password', pass);
            setValue('resetPass', resetPass);
            if (encryptedPassword !== '')
                setValue('encryptedPassword', encryptedPassword);
        })
    }

    const onSendUsername = () => {
        sendUsername(props.employee.username, props.employee.employeeID).then((res) => {
            let status = res?.status;
            let data = res?.data?.[0];
            let isError = data?.isError ?? '';
            let message = data?.message ?? '';
            if (status === 200) {
                showMessage({
                    message: t(`common:${message}`),
                    type: isError ? "danger" : "success",
                    duration: 3000,
                })
            }
            else {
                showMessage({
                    message: t(`common:emailNotSendErr`),
                    type: isError ? "danger" : "success",
                    duration: 3000,
                })
            }
        })
    }
    //#endregion

    //#region Loading
    if (isLoading) {
        return (
            <SafeAreaView
                style={{ justifyContent: 'center' }}>
                <ActivityIndicator animating={true} size='large' color="#86BC25" />
            </SafeAreaView>
        )
    }
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <ScrollView>
                <SafeAreaView style={styles.row}>
                    <SafeAreaView style={styles.column}>
                        <SafeAreaView style={[styles.buttons]}>
                            <CustomButton
                                text={t('common:issuePassword')}
                                onPress={() => onIssuePassword()}
                                type='BUTTON_SAVE'
                                isGradient={true}
                                textTop='0'
                                sz={15}
                            />
                            <CustomButton
                                text={t('common:sendUsername')}
                                onPress={() => onSendUsername()}
                                type='BUTTON_SAVE'
                                isGradient={true}
                                textTop='0'
                                sz={15}
                            />
                            <View style={styles.verticalLine} />
                            <CustomButton
                                text={t('common:save')}
                                onPress={handleSubmit(onSubmit, onErrorsFound)}
                                type='BUTTON_SAVE'
                                isGradient={true}
                                textTop='0'
                                sz={15}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={`${t("common:employee")}`}
                                type='COMPANY'
                            />
                            <CustomControlledInput
                                name={'name'}
                                control={control}
                                editable={false}
                                type={showErrors?.name ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={t("common:username")}
                                type='COMPANY'
                            />
                            <CustomControlledInput
                                name={'username'}
                                control={control}
                                editable={true}
                                type={showErrors?.username ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                                rules={{
                                    required: t('common:nameReqMess'),
                                }}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={t("common:userRole")}
                                type='COMPANY'
                            />
                            <CustomInput
                                value={selectedUserRole}
                                editable={false}
                                type={showErrors?.userroleid ? 'EDIT_EMPLOYEE_REQUIRED' : 'USER_ROLES'}
                                placeholder={`Please Choose a User Role...`}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={t("common:password")}
                                type='COMPANY'
                            />
                            <CustomControlledInput
                                name={'password'}
                                control={control}
                                editable={true}
                                type={showErrors?.password ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={t("common:encyptedPassword")}
                                type='COMPANY'
                            />
                            <CustomControlledInput
                                name={'encyptedPassword'}
                                control={control}
                                editable={false}
                                type={showErrors?.encyptedPassword ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={t("common:reqRestPassOnLogin")}
                                type='COMPANY'
                            />
                            <CustomControlledCheckbox
                                control={control}
                                name={'resetPass'}
                                type={showErrors?.resetPass ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel
                                value={t("common:lock")}
                                type='COMPANY'
                            />
                            <CustomControlledCheckbox
                                control={control}
                                name={'lockUser'}
                                type={showErrors?.lockUser ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                    <SafeAreaView style={styles.column}>
                        {/* search */}
                        <CustomInput
                            value={searchQuery || ""}
                            editable={true}
                            setValue={setSearchQuery}
                            type='SELECT_EMPLOYEE'
                            placeholder={`${t('common:search')} for User Roles...`}
                        />
                        <SafeAreaView style={[styles.cardView]}>
                            {userRoles.length > 0
                                ?
                                <FlatList
                                    contentContainerStyle={[{
                                        paddingHorizontal: 10,
                                    }]}
                                    style={{ height: height * 0.5 }}
                                    data={userRoles}
                                    keyExtractor={(item, index) => `${item?.userroleid}-${index}`}
                                    renderItem={({ item }) => (
                                        <SafeAreaView>
                                            <Controller
                                                control={control}
                                                name="userroleid"
                                                render={({ field: { onChange } }) => (
                                                    <Pressable
                                                        onPress={() => { onChange(item.value); setSelectedUserRole(item.label) }}
                                                        style={() => [item.value === useWatch({ control, name: 'userroleid' }) ? styles.selectedItem : null]}
                                                    >
                                                        <SafeAreaView style={styles.employeeContainer}>
                                                            <CustomLabel
                                                                value={item.label}
                                                                type='IMAGE_TEXT'
                                                            />
                                                        </SafeAreaView>
                                                    </Pressable>
                                                )}
                                                rules={{
                                                    required: '*',
                                                }}
                                            />
                                        </SafeAreaView>
                                    )}
                                    scrollEnabled={true}
                                />
                                :
                                searchQuery
                                    ?
                                    <SafeAreaView
                                        style={styles.cardEmployees}>
                                        <CustomLabel
                                            value={t("common:noResultsFound")}
                                            type="ASSM"
                                        />
                                    </SafeAreaView>
                                    :
                                    null
                            }
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </ScrollView >
        </SafeAreaView >

    )
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        flex: 1,
        alignItems: 'center'
    },
    mainView: {
        flex: 1,
        height: '100%'
    },
    subMain: {
        margin: 10,
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardstyleCompany: {
        margin: 10,
        borderRadius: 5,
        backgroundColor: colors.background_card,
    },
    cardStyleBranch: {
        margin: 30,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
        flex: 1,
    },
    smallInputBox: {
        // width: '5%',
        flex: 1,
        margin: 10,
    },
    bigInputBox: {
        width: '30%',
        flex: 1
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
        margin: 5,
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        width: '55%',
        margin: 15,
    },
    cardstyle: {
        padding: 10,
        width: "100%",
        borderRadius: 35,
        backgroundColor: colors.background_card,
        elevation: 0,
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    verticalLine: {
        backgroundColor: colors.buttons_primary,
        height: '100%',
        width: 1,
    },
    selectedItem: {
        borderWidth: 4,
        borderColor: colors.buttons_primary,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
    },
    employeeContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 3,
    },
    column: {
        width: '50%',
    },
    cardView: {
        flex: 1,
        margin: '2%',
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        height: '80%'
    },
    cardEmployees: {
        flex: 1,
        marginTop: "1%",
        marginLeft: "2%",
        marginRight: "2%",
    },
});


//#endregion

export default HRAdminUserRoles;